import React from 'react'
import { View, StyleSheet, Text, Image, Link } from '@react-pdf/renderer'

import * as theme from 'src/parser/shared/PDF/theme'
import Section from 'src/parser/shared/PDF/Section'
import { TAlarmReportProps } from 'src/parser/shared/PDF/Provider'
import { TAttachmentWithURI } from './Provider/useAttachments'
import Loadable from 'src/utils/Loadable'
import { TFunction } from 'i18next'
import { absurd } from 'fp-ts/lib/function'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

export type TProps = TAlarmReportProps

const Attachments = ({
  attachments,
  additionalDetails,
}: TProps): JSX.Element => {
  const items = parseAttachmentItems({
    attachments,
    additionalDetails,
  }).sort(groupByAttachmentKindSortComparer)

  return (
    <Section title='alarm_report.attachments.title'>
      {items.map((it, index): JSX.Element => {
        switch (it._tag) {
          case 'image':
          case 'video':
          case 'audio':
          case 'document':
            return (
              <ItemWrapper key={it.key}>
                {it._status === 'failure' ? (
                  <ItemError {...it} />
                ) : (
                  <>
                    <Text style={styles.itemName}>{it.name}</Text>

                    {it._tag === 'image' && typeof it.imageSrc === 'string' && (
                      <Image
                        style={styles.itemImage}
                        source={it.imageSrc}
                      />
                    )}

                    {'url' in it && typeof it.url === 'string' && (
                      <Link src={it.url}>
                        <Text style={styles.itemUrl}>{it.url}</Text>
                      </Link>
                    )}
                  </>
                )}
              </ItemWrapper>
            )
          case 'error':
            return (
              <ItemWrapper key={`error_${index}`}>
                <ItemError {...it} />
              </ItemWrapper>
            )
          default:
            return absurd(it)
        }
      })}
    </Section>
  )
}

const ItemWrapper = ({
  key,
  children,
}: {
  key: string
  children: React.ReactNode
}): JSX.Element => {
  return (
    <View
      wrap={false}
      key={key}
      style={styles.item}
    >
      {children}
    </View>
  )
}

const ItemError = ({ name, renderMessage }: TItemError): JSX.Element => {
  const { t } = useTranslation()

  return (
    <>
      {typeof name === 'string' && <Text style={styles.itemName}>{name}</Text>}
      <Text style={styles.itemError}>{renderMessage(t)}</Text>
    </>
  )
}

const groupByAttachmentKindSortComparer = (
  a: TParsedAttachmentItem,
  b: TParsedAttachmentItem,
): number => {
  return ATTACHMENT_TYPES[b._tag] - ATTACHMENT_TYPES[a._tag]
}

const styles = StyleSheet.create({
  item: {
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  itemName: {
    ...theme.styles.subtitle,
    textTransform: undefined,
  },
  itemUrl: {
    ...theme.styles.subtitle,
    fontWeight: 'normal',
    textTransform: undefined,
  },
  itemError: {
    ...theme.styles.text,
    color: theme.colors.error,
  },
  itemImage: {
    marginTop: theme.padding(1),
    height: 300,
    alignSelf: 'flex-start',
  },
})

const parseAttachmentItems = ({
  additionalDetails,
  attachments,
}: Pick<TProps, 'additionalDetails' | 'attachments'>): TParsedAttachmentItem[] => {
  const parsedItems: TParsedAttachmentItem[] = []

  if (Loadable.isFailure(additionalDetails)) {
    parsedItems.push({
      _tag: 'error',
      error: additionalDetails.error,
      renderMessage: t => t('alarm_report.attachments.errors.fetchFailed'),
    })
  } else {
    const {
      audioUrls = [],
      videoUrls = [],
      imageUrls = [],
    } = additionalDetails.data

    const additionalDetailsByType: Array<[TAttachmentItem['_tag'], string[]]> = [
      ['audio', audioUrls],
      ['video', videoUrls],
      ['image', imageUrls],
    ]
    additionalDetailsByType.forEach(([type, urls]) => {
      parsedItems.concat(urls.map((url, i) => {
        const result: TParsedAttachmentItem = {
          _status: 'success' as const,
          _tag: type,
          key: `${url}_${i}`,
          name: getResourceNameFromUrl(url),
          url,
        }

        if (type === 'image')
          return {
            ...result,
            imageSrc: url,
          }
        return result
      }))
    })
  }

  if (Loadable.isFailure(attachments)) {
    parsedItems.push({
      _tag: 'error',
      error: attachments.error,
      renderMessage: t => t('alarm_report.attachments.errors.fetchFailed'),
    })
  } else {
    attachments.data.forEach(it => {
      const parse = it.type in ATTACHMENT_TYPES
        ? ATTACHMENT_TYPES[it.type].parse
        : it.type.endsWith('Url')
        ? urlAttachmentToItem
        : makeAttachmentErrorItem

      const parsed = parse(it)
      if (parsed !== null) parsedItems.push(parsed)
    })
  }

  return parsedItems
}

const ATTACHMENT_TYPES = {
  Image: {
    tag: 'image',
    order: 1,
    parse: attachment => {
      if (attachment.dataURI === null) return null

      const name = getAttachmentName(attachment)
      const imageSrc = attachment.dataURI?.map(it =>
        it.replace('image/pjpeg', 'image/jpeg'),
      )

      return imageSrc.match(
        imageSrc => ({
          _tag: 'image',
          _status: 'success',
          key: attachment.id,
          name,
          imageSrc,
        }),
        error => ({
          _tag: 'image',
          _status: 'failure',
          key: attachment.id,
          error,
          renderMessage: t => t('alarm_report.attachments.errors.invalidData'),
        }),
      )
    }
  },
  Video: {
    tag: 'video',
    order: 2,
    parse: it => {
      return {
        _tag: 'video',
        _status: 'success',
        key: it.id,
        name: getAttachmentName(it),
      }
    },
  },
  Audio: {
    tag: 'audio',
    order: 3,
    parse: it => {
      return {
        _tag: 'audio',
        _status: 'success',
        key: it.id,
        name: getAttachmentName(it),
      }
    },
  },
  Document: {
    tag: 'document',
    order: 4,
    parse: it => {
      return {
        _tag: 'document',
        _status: 'success',
        key: it.id,
        name: getAttachmentName(it),
      }
    },
  }
} satisfies {
  [Type in TAttachmentType]: {
    tag: Uncapitalize<Type>
    order: number
    parse: (data: TAttachmentWithURI) => TParseResult<Uncapitalize<Type>>
  }
}

type TAttachmentType = 'Image' | 'Video' | 'Audio' | 'Document'
type TAttachmentItemSuccessTag = Uncapitalize<TAttachmentType>

type TItemSuccessBase = {
  _status: 'success'
  key: string
  name: string
  url?: string
  imageSrc?: string
}

type TItemError = {
  error: Error
  name?: string
  renderMessage: (t: TFunction) => string
}

type TParseResult<Tag extends TAttachmentItemSuccessTag> =
  | (TItemSuccessBase & { _tag: Tag })
  | TFailureItem<Tag>
  | TErrorItem
  | null

type TAttachmentItem = {
  [K in keyof typeof ATTACHMENT_TYPES]: Exclude<ReturnType<typeof ATTACHMENT_TYPES[K]['parse']>, null>
}[keyof typeof ATTACHMENT_TYPES]

type TParsedAttachmentItem =
  | TAttachmentItem
  | (TItemSuccessBase & { _tag: typeof ATTACHMENT_TAGS[number] })
  | TFailureItem
  | TErrorItem

type TFailureItem<T extends TAttachmentItemSuccessTag = TAttachmentItemSuccessTag> = {
  [Tag in T]: {
    _status: 'failure'
    _tag: Tag
    key: string
  } & Omit<TItemError, '_tag'>
}[T]

type TErrorItem = { _tag: 'error' } & TItemError

const ATTACHMENT_TAGS = Object.values(ATTACHMENT_TYPES).map(it => it.tag)

const getResourceNameFromUrl = (url = ''): string => {
  const index = url.lastIndexOf('/') + 1
  const name = url.substr(index)

  return name
}

const makeAttachmentErrorItem = (
  attachment: TAttachmentWithURI,
): TErrorItem => {
  return {
    _tag: 'error',
    name: getAttachmentName(attachment),
    error: new Error(`Unsupported attachment type "${attachment.type}".`),
    renderMessage: t =>
      t('alarm_report.attachments.errors.unsupportedType', {
        type: attachment.type,
      }),
  }
}

const capitalize = (it: string): string => {
  return it.charAt(0).toUpperCase() + it.slice(1)
}

const urlAttachmentToItem = (
  attachment: TAttachmentWithURI,
): Extract<
  TParsedAttachmentItem,
  { _tag: typeof ATTACHMENT_TAGS[number] }
> | TErrorItem => {
  const type = ATTACHMENT_TAGS.find(
    it => `${capitalize(it)}Url` === attachment.type,
  )

  if (typeof type !== 'string') {
    return makeAttachmentErrorItem(attachment)
  }

  const url =
    typeof attachment.data?.url === 'string' ? attachment.data.url : undefined

  return {
    _tag: type,
    _status: 'success',
    key: attachment.id,
    name: getAttachmentName(attachment),
    url,
    imageSrc: url,
  }
}

const getAttachmentName = (attachment: TAttachmentWithURI): string => {
  const attachmentAsUrlType = SAttachmentWithURL.safeParse(attachment)
  const name = attachmentAsUrlType.success
    ? getResourceNameFromUrl(attachmentAsUrlType.data.data.url)
    : attachment.file?.name ?? 'unnamed'

  return `${name} (${attachment.id.slice(0, 8)})`
}

const SAttachmentWithURL = z.object({
  type: z.string().endsWith('Url'),
  data: z.object({
    url: z.string(),
  }),
})

export default Attachments
